import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from "react-select";
import Swal from 'sweetalert2';
import "./AddUnavailableDate.css";
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

const generateTimeOptions = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute of [0, 30]) {
      const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      times.push({ value, label: value });
    }
  }
  return times;
};

const AddUnavailableDate = () => {
  const token = localStorage.getItem('auth-token');

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const courtId = queryParams.get("courtId");
  const unavailableDateId = queryParams.get("unavailableDateId");
  const locationStartDate = queryParams.get("startDate");
  const locationEndDate = queryParams.get("endDate");

  const isEditMode = !!unavailableDateId;

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);

  useEffect(() => {
    if (locationStartDate) {
      const [date, time] = locationStartDate.split(" ");
      setStartDate(date);
      setStartTime({ value: time, label: time });
    }
    if (locationEndDate) {
      const [date, time] = locationEndDate.split(" ");
      setEndDate(date);
      setEndTime({ value: time, label: time });
    }
  }, [locationStartDate, locationEndDate]);

  const allTimeOptions = generateTimeOptions();

  const filteredEndTimeOptions = allTimeOptions.filter(
    (option) => !startTime || option.value > startTime.value
  );

  const handleStartDateChange = (e) => {
    const selectedDate = e.target.value;
    setStartDate(selectedDate);
    if (endDate && selectedDate > endDate) {
      setEndDate("");
    }
  };

  const handleStartTimeChange = (selectedOption) => {
    setStartTime(selectedOption);
    if (endTime && selectedOption.value >= endTime.value) {
      setEndTime(null);
    }
  };

  const navigate = useNavigate();

  const buildSubmitPayload = () => ({
    id: unavailableDateId || null,
    courtId,
    startDate: startDate && startTime ? `${startDate} ${startTime.value}` : "",
    endDate: endDate && endTime ? `${endDate} ${endTime.value}` : "",
  });

  const redactForAudit = (payload) => {
    if (!payload) return payload;
    return { ...payload };
  };

  useEffect(() => {
    if (initializedBaselineRef.current) return;
    const readyForBaseline = isEditMode
      ? Boolean(startDate && endDate && startTime && endTime) 
      : true;
    if (readyForBaseline) {
      baselineRef.current = buildSubmitPayload();
      initializedBaselineRef.current = true;
    }
  }, [isEditMode, startDate, endDate, startTime, endTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!courtId || !startDate || !endDate || !startTime || !endTime) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill all fields before submitting.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const newPayload = buildSubmitPayload();
    const oldPayload = baselineRef.current || {};

    if (isEditMode) {
      const { digest, html } = buildDiffPreview(redactForAudit(oldPayload), redactForAudit(newPayload));
      if (!digest.length) {
        Swal.fire({
          icon: 'info',
          title: 'Nothing to update',
          text: 'No differences detected.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2500,
        });
        return;
      }
      const confirm = await Swal.fire({
        title: 'Apply these changes?',
        html,
        showCancelButton: true,
        confirmButtonText: 'Update',
      });
      if (!confirm.isConfirmed) return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ADD_COURT_UNAVAILABLE_DATE}/${courtId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(newPayload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.success_message || `Error: ${response.statusText}`);
      }

      fireAudit({
        entity: 'court-unavailability',
        affected_id: unavailableDateId || null,
        action: isEditMode ? 'update' : 'create',
        businessApi: `${process.env.REACT_APP_API_ADD_COURT_UNAVAILABLE_DATE}/${courtId}`,
        businessMethod: 'POST',
        oldPayload: redactForAudit(oldPayload),
        newPayload: redactForAudit(newPayload),
        section: 'Courts',
        subSection: 'Unavailable Dates',
        extra: { page: 'AddUnavailableDate' },
      });

      baselineRef.current = newPayload; 

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Unavailable date added successfully!",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      navigate(-1);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to add unavailable date. Please try again.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="enrollment-container" style={{ overflow: 'visible' }}>
      <div className="breadcrumb">
        <Link to="/courtlist" className="breadcrumb-link">
          Court List
        </Link>{" "}
        &gt; {isEditMode ? 'Edit Unavailable Date' : 'New Unavailable Date'}
      </div>

      <form onSubmit={handleSubmit} className="walkin-enquiry-form">
        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="date"
            id="startDate"
            name="startDate"
            className="walkin-form has-value"
            value={startDate}
            onChange={handleStartDateChange}
            min={isEditMode ? startDate || today : today}
            required
            onClick={(e) => e.target.showPicker()}
          />
          <label htmlFor="startDate">Start Date</label>
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <input
            type="date"
            id="endDate"
            name="endDate"
            className="walkin-form has-value"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            min={startDate || today}
            required
            onClick={(e) => e.target.showPicker()}
          />
          <label htmlFor="endDate">End Date</label>
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <Select
            id="startTime"
            className={`walkin-form-dropdown ${startTime ? 'has-value' : ''}`}
            options={allTimeOptions}
            value={startTime}
            onChange={handleStartTimeChange}
            placeholder="Select Start Time"
            required
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {startTime && <label htmlFor="startTime">Start Time</label>}
        </div>

        <div className="floating-select-form" style={{ width: '98%', position: 'relative' }}>
          <Select
            id="endTime"
            className={`walkin-form-dropdown ${endTime ? 'has-value' : ''}`}
            options={filteredEndTimeOptions}
            value={endTime}
            onChange={setEndTime}
            placeholder="Select End Time"
            required
            isDisabled={!startTime}
            styles={{
              control: (provided) => ({ ...provided, padding: '5px' }),
              placeholder: (provided) => ({ ...provided, color: 'black' }),
            }}
          />
          {endTime && <label htmlFor="endTime">End Time</label>}
        </div>

        <button type="submit" className="submit-button" disabled={isSubmitting}>
          {isEditMode ? (isSubmitting ? 'Saving...' : 'Save Changes') : (isSubmitting ? 'Submitting...' : 'Submit')}
        </button>
      </form>
    </div>
  );
};

export default AddUnavailableDate;
